/* Coral Triangle: shared resort enquiry pricing. Values are retail display rates. */
(function (scope) {
  'use strict';
  const clean = (v) => String(v == null ? '' : v).trim();
  const text = (v) => clean(v).toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ');
  const round = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
  const positiveInt = (v, fallback = 0) => Number.isInteger(Number(v)) && Number(v) > 0 ? Number(v) : fallback;

  function parseNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
    let s = clean(value).replace(/[\s\u00a0\u202f'’]/g, '');
    if (!/^\d+(?:[.,]\d+)*$/.test(s)) return null;
    const comma = s.lastIndexOf(',');
    const dot = s.lastIndexOf('.');
    if (comma >= 0 && dot >= 0) {
      const decimal = comma > dot ? ',' : '.';
      const grouping = decimal === ',' ? '.' : ',';
      const parts = s.split(decimal);
      if (parts.length !== 2 || !/^\d{1,2}$/.test(parts[1])) return null;
      if (!new RegExp('^\\d{1,3}(?:\\' + grouping + '\\d{3})*$').test(parts[0])) return null;
      s = parts[0].split(grouping).join('') + '.' + parts[1];
    } else if (comma >= 0 || dot >= 0) {
      const separator = comma >= 0 ? ',' : '.';
      const parts = s.split(separator);
      if (parts.length === 2 && parts[1].length <= 2) s = parts.join('.');
      else if (parts[0].length <= 3 && parts.slice(1).every((part) => part.length === 3)) s = parts.join('');
      else return null;
    }
    const result = Number(s);
    return Number.isFinite(result) && result >= 0 ? result : null;
  }

  const currencyToken = '(?:US\\$|A\\$|S\\$|\\bEUR\\b|\\bUSD\\b|\\bGBP\\b|\\bAUD\\b|\\bSGD\\b|\\bIDR\\b|\\bPHP\\b|\\bMYR\\b|\\bPGK\\b|€|\\$|£)';
  const amountToken = "\\d+(?:(?:[.,'’]|[ \\u00a0\\u202f](?=\\d{3}(?:\\D|$)))\\d+)*";
  function currencyCode(token) {
    const value = clean(token).toUpperCase();
    return ({ '€': 'EUR', '$': 'USD', 'US$': 'USD', '£': 'GBP', 'A$': 'AUD', 'S$': 'SGD' })[value] || value;
  }
  function extractMoney(raw, { allowBare = false, currency = 'EUR' } = {}) {
    if (typeof raw === 'number') return allowBare && parseNumber(raw) !== null ? { amount: raw, currency, explicitZero: raw === 0 } : null;
    const value = clean(raw);
    const matches = [];
    for (const match of value.matchAll(new RegExp('(' + currencyToken + ')\\s*(' + amountToken + ')', 'gi'))) {
      matches.push({ index: match.index, end: match.index + match[0].length, amount: parseNumber(match[2]), currency: currencyCode(match[1]) });
    }
    for (const match of value.matchAll(new RegExp('(' + amountToken + ')\\s*(' + currencyToken + ')', 'gi'))) {
      if (!matches.some((m) => match.index < m.end && match.index + match[0].length > m.index)) {
        matches.push({ index: match.index, end: match.index + match[0].length, amount: parseNumber(match[1]), currency: currencyCode(match[2]) });
      }
    }
    matches.sort((a, b) => a.index - b.index);
    if (!matches.length) {
      const amount = allowBare ? parseNumber(value) : null;
      return amount === null ? null : { amount, currency, explicitZero: amount === 0 };
    }
    const first = matches[0];
    if (first.amount === null || /^\s*[-–—]\s*\d/.test(value.slice(first.end))) return null;
    // Different amounts in the same currency need a chosen tariff, not the first number.
    if (matches.some((m) => m.currency === first.currency && m.amount !== first.amount)) return null;
    return { amount: first.amount, currency: first.currency, explicitZero: first.amount === 0 };
  }
  const numericRate = (value) => extractMoney(value, { allowBare: true })?.amount ?? 0;
  const currencyFor = (value, fallback = 'EUR') => extractMoney(value, { allowBare: true, currency: fallback })?.currency || fallback;
  function formatMoney(currency, amount) {
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount); }
    catch (error) { return `${currency || 'EUR'} ${round(amount)}`; }
  }
  function normalizePriceUnit(raw) {
    const value = text(raw).replace(/[\s-]+/g, '_');
    if (['per_room','room','room_night','per_accommodation','accommodation','per_unit','unit'].includes(value)) return 'per_room';
    if (['per_person','person','per_guest','guest','per_pax','pax','pp','diver','per_diver'].includes(value)) return 'per_person';
    if (['fixed','per_stay','stay','per_booking','booking','total'].includes(value)) return 'fixed';
    return '';
  }
  function detectPriceUnit(raw) {
    const value = text(raw);
    if (/per\s*(?:person|guest|pax|diver|participant)\b|\bpp\b|\bp\.p\./.test(value)) return 'per_person';
    if (/per\s*(?:stay|booking)\b|fixed\s*(?:price|rate)\b|total\s*(?:price|rate)\b/.test(value)) return 'fixed';
    const withoutOccupancy = value.replace(/\b(?:\d+\s*)?(?:guests?|persons?|pax)\s+per\s+room\b/g, '');
    if (/(?:per|\/)\s*(?:room|suite|villa|bungalow|cabana|cabin|cottage|chalet|apartment|house|studio|unit|accommodation)\b|\broom\s*(?:from|rate|price|\/\s*night)\b/.test(withoutOccupancy)) return 'per_room';
    return '';
  }
  const priceUnitFor = (explicit, basis, rate) => normalizePriceUnit(explicit) || detectPriceUnit(basis) || detectPriceUnit(rate);
  function packageNightsFromText(raw) {
    const value = text(raw).replace(/\bminimum\s+(?:stay\s*)?(?:of\s*)?\d+\s*[- ]?nights?\b/g, '');
    const match = value.match(/\b\d+\s*[- ]?days?\s*(?:\/|and|,)\s*(\d+)\s*[- ]?nights?\b/)
      || value.match(/\b(\d+)\s*[- ]?nights?\s+(?:(?:relaxed|diver|non-diver|dive|diving|stay|all-inclusive)\s+){0,4}package\b/)
      || value.match(/\bpackage\s*(?:of|for)?\s*(\d+)\s*[- ]?nights?\b/)
      || value.match(/(?:\/|for)\s*(\d+)\s*[- ]?nights?\b/);
    return match ? positiveInt(match[1]) : 0;
  }
  function minimumStayFromText(raw) {
    const match = text(raw).match(/\bminimum\s+(?:stay\s*)?(?:of\s*)?(\d+)\s*[- ]?nights?\b/);
    return match ? positiveInt(match[1], 1) : 1;
  }
  function normalizePricingMode(raw) {
    const value = text(raw).replace(/[\s-]+/g, '_');
    if (['per_night','nightly','night','night_rate'].includes(value)) return 'per_night';
    if (['fixed_package','package','fixed'].includes(value)) return 'fixed_package';
    if (['package_plus_extra_night','package_plus_extra','package_extension','package_with_extensions'].includes(value)) return 'package_plus_extra_night';
    if (['tiered_package','tiered','package_tiers','tiered_packages'].includes(value)) return 'tiered_package';
    return '';
  }
  function parseTierPrices(raw, fallbackCurrency = 'EUR') {
    const value = clean(raw);
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((tier) => {
        const money = extractMoney(tier.price ?? tier.amount, { allowBare: true, currency: tier.currency || fallbackCurrency });
        return { nights: positiveInt(tier.nights || tier.duration), price: money?.amount, currency: money?.currency };
      }).filter((tier) => tier.nights > 0 && tier.price > 0).sort((a, b) => a.nights - b.nights);
    } catch (error) { /* Legacy text tiers below. */ }
    return value.split(/\s*(?:\||;|\n)\s*/).map((chunk) => {
      const duration = chunk.match(/(\d+)\s*[- ]?nights?/i) || chunk.match(/^\s*(\d+)\s*[:=–—-]/);
      const money = extractMoney(chunk);
      return { nights: positiveInt(duration?.[1]), price: money?.amount, currency: money?.currency || fallbackCurrency };
    }).filter((tier) => tier.nights && tier.price > 0).sort((a, b) => a.nights - b.nights);
  }
  function calendarDay(raw) {
    const value = clean(raw);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const stamp = Date.parse(value + 'T00:00:00Z');
    return Number.isFinite(stamp) && new Date(stamp).toISOString().slice(0, 10) === value ? stamp / 86400000 : null;
  }
  // Structured public room tariffs. Missing data preserves the legacy pricing
  // path; malformed supplied data must never fall back to an arbitrary rate.
  function parseNightlyRates(raw) {
    if (raw == null || clean(raw) === '' || clean(raw) === 'null') return { present: false };
    const invalid = { present: true, valid: false };
    try {
      const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!data || data.version !== 1 || data.basis !== 'room_per_night'
        || !/^[A-Z]{3}$/.test(data.currency || '')
        || !Number.isSafeInteger(data.minimum_nights) || data.minimum_nights < 1
        || !Number.isSafeInteger(data.maximum_guests) || data.maximum_guests < 1
        || !Array.isArray(data.bands) || !data.bands.length) return invalid;
      const firstDay = calendarDay(data.valid_from);
      const lastDay = calendarDay(data.valid_until);
      if (firstDay === null || lastDay === null || lastDay < firstDay) return invalid;
      const bands = data.bands.map((band) => {
        const min = band.min_nights;
        const max = band.max_nights == null ? Infinity : band.max_nights;
        if (!Number.isSafeInteger(min) || min < data.minimum_nights
          || (max !== Infinity && (!Number.isSafeInteger(max) || max < min))
          || !Array.isArray(band.occupancy) || !band.occupancy.length) throw Error('Invalid nightly band');
        const prices = {};
        band.occupancy.forEach(({ guests, nightly_price: amount }) => {
          if (!Number.isSafeInteger(guests) || guests < 1 || guests > data.maximum_guests
            || typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0
            || Object.prototype.hasOwnProperty.call(prices, guests)) throw Error('Invalid occupancy tariff');
          prices[guests] = amount;
        });
        return { min, max, prices };
      }).sort((a, b) => a.min - b.min);
      if (bands.some((band, i) => i && band.min <= bands[i - 1].max)) return invalid;
      return { present: true, valid: true, currency: data.currency,
        minimumNights: data.minimum_nights, maximumGuests: data.maximum_guests,
        firstDay, lastDay, validFrom: data.valid_from, validUntil: data.valid_until,
        adultRates: data.guest_basis === 'adult', extraBedFrom: positiveInt(data.extra_bed_from), bands };
    } catch (error) { return invalid; }
  }
  function priceNightlyRooms(rates, selected, nights, context, base) {
    const unresolved = (pricingNote) => ({ ...base, pricingNote });
    if (!rates.valid) return unresolved('The room tariff needs confirmation before an estimate can be shown.');
    if (!Number.isSafeInteger(nights) || nights < rates.minimumNights) return unresolved(`Minimum stay: ${rates.minimumNights} nights`);
    const { units, guests } = selected;
    if (!Number.isSafeInteger(units) || units < 1 || !Number.isSafeInteger(guests)
      || guests < units || guests > units * rates.maximumGuests) return unresolved('Check the number of rooms and guests.');
    const arrival = clean(context.checkIn);
    if (arrival) {
      const firstNight = calendarDay(arrival);
      if (firstNight === null) return unresolved('Choose a valid arrival date.');
      if (firstNight < rates.firstDay || firstNight + nights - 1 > rates.lastDay) {
        return unresolved(`Published rates cover nights from ${rates.validFrom} to ${rates.validUntil}. Other dates need a quote.`);
      }
    }
    const band = rates.bands.find((item) => nights >= item.min && nights <= item.max);
    if (!band) return unresolved('This stay length needs a separate quotation.');
    // The selector captures total guests per category. Distribute them evenly
    // across those rooms, without fractional occupancy or empty-room tariffs.
    const low = Math.floor(guests / units);
    const highCount = guests % units;
    const breakdown = [{ guests: low, rooms: units - highCount }, { guests: low + 1, rooms: highCount }]
      .filter((item) => item.rooms > 0).map((item) => ({ ...item, nightlyRate: band.prices[item.guests] }));
    if (breakdown.some((item) => !(item.nightlyRate > 0))) return unresolved('The rate for this room occupancy needs confirmation.');
    const total = round(breakdown.reduce((sum, item) => sum + item.rooms * item.nightlyRate * nights, 0));
    if (!Number.isFinite(total)) return unresolved('Check the stay details.');
    const pricingLabel = breakdown.map((item) => `${item.rooms} room${item.rooms === 1 ? '' : 's'} with ${item.guests} guest${item.guests === 1 ? '' : 's'} at ${formatMoney(rates.currency, item.nightlyRate)}/night`).join('; ');
    const notes = [`${pricingLabel} × ${nights} nights.`];
    if (rates.adultRates) notes.push('Adult rates; children are quoted separately.');
    if (units > 1) notes.push('Guests are shared evenly between the selected rooms.');
    if (rates.extraBedFrom && breakdown.some((item) => item.guests >= rates.extraBedFrom)) notes.push('Extra beds are subject to availability.');
    if (!arrival) notes.push(`Tariff dates: ${rates.validFrom} to ${rates.validUntil}; select arrival to check coverage.`);
    return { ...base, currency: rates.currency, lineTotal: total, pricingResolved: true,
      pricingLabel, pricingNote: notes.join(' '), nightlyBreakdown: breakdown };
  }
  function pricingConfig(data) {
    const rateBasis = data.roomRateBasis || '';
    const rateText = data.roomRate || '';
    const publishedGuests = text(rateBasis + ' ' + rateText).match(/\b(?:price|rate)\s+(?:includes|covers)\s+(?:up to\s+)?(\d+)\s+guests?\b/);
    const includedGuests = positiveInt(data.roomIncludedGuests) || positiveInt(publishedGuests?.[1])
      || (positiveInt(data.roomMaxOccupancy, 2) > 2 ? 2 : 0);
    const currency = clean(data.roomCurrency) || currencyFor(rateText);
    const money = extractMoney(data.roomPriceAmount || rateText, { allowBare: true, currency });
    const startingRate = money?.amount ?? null;
    const nightlyRates = parseNightlyRates(data.roomNightlyRates);
    const minimumStay = Math.max(positiveInt(data.roomMinimumStay, 1), minimumStayFromText(rateBasis + ' ' + rateText), nightlyRates.valid ? nightlyRates.minimumNights : 1);
    const packageNights = positiveInt(data.roomPackageNights) || packageNightsFromText(rateText) || packageNightsFromText(rateBasis);
    const packageMoney = extractMoney(data.roomPackagePrice, { allowBare: true, currency: money?.currency || currency });
    const extraNightRate = extractMoney(data.roomExtraNightRate, { allowBare: true, currency: money?.currency || currency })?.amount ?? 0;
    const tiers = parseTierPrices(data.roomPackageTiers, money?.currency || currency);
    const mode = nightlyRates.present ? 'occupancy_nightly' : normalizePricingMode(data.roomPricingMode) || (tiers.length ? 'tiered_package' : packageNights ? (extraNightRate > 0 ? 'package_plus_extra_night' : 'fixed_package') : 'per_night');
    return {
      mode, unit: nightlyRates.present ? 'per_room' : priceUnitFor(data.roomPriceUnit, rateBasis, rateText), rateBasis, nightlyRates,
      startingRate, packageNights, packagePrice: packageMoney?.amount ?? startingRate,
      extraNightRate, tiers, minimumStay, currency: nightlyRates.valid ? nightlyRates.currency : money?.currency || currency,
      packageOnly: /package component|no standalone|included in (?:selected|all supplied)/i.test(rateText + ' ' + rateBasis),
      includedGuests,
      extraGuestRate: extractMoney(data.roomExtraGuestRate, { allowBare: true, currency })?.amount ?? null
    };
  }
  function priceRoom(cfg, selected, nights, context = {}) {
    const base = { currency: cfg.currency, lineTotal: null, pricingResolved: false, pricingMode: cfg.mode, priceUnit: cfg.unit, packageNights: cfg.packageNights, extraNightRate: cfg.extraNightRate, pricingLabel: cfg.rateBasis, pricingNote: '' };
    if (cfg.mode === 'occupancy_nightly') return priceNightlyRooms(cfg.nightlyRates, selected, nights, context, base);
    if (cfg.packageOnly) return { ...base, pricingNote: 'This room is part of a stay package. Select the package you would like quoted.' };
    if (!cfg.unit) return { ...base, pricingNote: 'The rate per person or per room needs confirmation for this selection.' };
    if (nights < cfg.minimumStay) return { ...base, pricingNote: `Minimum stay: ${cfg.minimumStay} nights` };
    const multiplier = cfg.unit === 'per_room' ? selected.units : cfg.unit === 'fixed' ? 1 : selected.guests;
    if (cfg.includedGuests && selected.guests > cfg.includedGuests * selected.units && cfg.extraGuestRate === null) {
      return { ...base, pricingNote: 'The supplement for the additional guests will be included in your quote.' };
    }
    const extraGuests = cfg.includedGuests ? Math.max(0, selected.guests - cfg.includedGuests * selected.units) : 0;
    const supplement = extraGuests * (cfg.extraGuestRate || 0) * nights;
    const resolved = (amount, label, currency = cfg.currency) => ({ ...base, currency, lineTotal: round(amount * multiplier + supplement), pricingResolved: true, pricingLabel: label });
    if (cfg.mode === 'per_night') {
      if (!(cfg.startingRate > 0)) return { ...base, pricingNote: 'A price for these dates and this room will be provided in your quote.' };
      return resolved(cfg.startingRate * nights, cfg.unit === 'per_room' ? 'per room/night' : cfg.unit === 'fixed' ? 'per night' : 'per person/night');
    }
    if (cfg.mode === 'tiered_package') {
      const tier = cfg.tiers.find((item) => item.nights === nights);
      if (tier) return resolved(tier.price, `${tier.nights}-night package`, tier.currency);
      const last = cfg.tiers[cfg.tiers.length - 1];
      if (last && nights > last.nights && cfg.extraNightRate > 0) return resolved(last.price + (nights - last.nights) * cfg.extraNightRate, `${last.nights}-night package + ${nights - last.nights} extra nights`, last.currency);
      return { ...base, pricingNote: cfg.tiers.length ? `Available package lengths: ${cfg.tiers.map((item) => item.nights).join(', ')} nights` : 'Choose a stay package to receive its price.' };
    }
    if (!cfg.packageNights || !(cfg.packagePrice > 0)) return { ...base, pricingNote: 'The package price and length will be confirmed in your quote.' };
    if (nights < cfg.packageNights) return { ...base, pricingNote: `Minimum package: ${cfg.packageNights} nights` };
    if (nights === cfg.packageNights) return resolved(cfg.packagePrice, `${cfg.packageNights}-night package`);
    if (cfg.extraNightRate > 0) return resolved(cfg.packagePrice + (nights - cfg.packageNights) * cfg.extraNightRate, `${cfg.packageNights}-night package + ${nights - cfg.packageNights} extra nights`);
    return { ...base, pricingNote: `The published package covers ${cfg.packageNights} nights. We will quote the additional nights.` };
  }
  function extraRole(extra) {
    const label = text(extra.label || extra.name);
    const category = text(extra.category || extra.type);
    const note = text(extra.publicNote);
    if (/^accommodation only\b/.test(label)) return 'stay_preference';
    if (/supplement|upgrade|extra bed|air.?conditioning|rental|babysitting/.test(label)) return 'extra';
    if (/dive|diving/.test(category) && /\b(?:included|bundled)\b/.test(note) && /\b(?:diver|stay|selected)\s+(?:\w+\s+)?package\b|\b\d+[- ]night\s+packages?\b/.test(note)) return 'stay_alternative';
    if (/^(?:accommodation|accommodation selection|non-diver stay|non-diver package|non-diver package|meal \/ stay|package|stay & dive package)$/.test(category)
      || /\bnon-diver\b.*\b(?:stay|package|sharing)\b|\b\d+[- ]night\s+non-diver/.test(label)) return 'stay_alternative';
    return 'extra';
  }
  function priceExtra(extra, context) {
    const note = clean(extra.publicNote);
    const label = text(extra.label || extra.name);
    const category = text(extra.category || extra.type);
    const basis = text(note);
    const combined = label + ' ' + basis;
    const role = extraRole(extra);
    const hasAmount = extra.priceAmount != null && clean(extra.priceAmount) !== '';
    const money = extractMoney(hasAmount ? extra.priceAmount : note, { allowBare: hasAmount, currency: extra.priceCurrency || 'EUR' });
    const base = { ...extra, role, priceAmount: money?.amount ?? null, priceCurrency: money?.currency || context.currency || 'EUR', priceBasis: '', lineTotal: null, pricingResolved: false };
    const unresolved = (reason) => ({ ...base, pricingNote: reason });
    const resolved = (multiplier, priceBasis) => multiplier > 0
      ? ({ ...base, priceBasis, lineTotal: round(money.amount * multiplier), pricingResolved: true })
      : unresolved('Tell us the number of participants for this service.');
    if (role === 'stay_alternative') return unresolved('Alternative stay package; a complete quote will replace the room estimate.');
    if (role === 'stay_preference') {
      if (/\b\d+\s*dives?\b|\bdiver package\b|diving included/i.test(context.roomBasis || '')) return unresolved('We will quote the accommodation-only version of the selected package.');
      return { ...base, priceBasis: 'selected rooms', lineTotal: 0, pricingResolved: true, pricingNote: 'Uses the selected room price.' };
    }
    if (!money) {
      const explicitlyIncluded = extra.included === true || /^(?:included|complimentary|free of charge|no charge)[.!]?$/.test(basis);
      if (explicitlyIncluded && !/dive programme|diving package/.test(category)) return { ...base, priceBasis: 'included', lineTotal: 0, pricingResolved: true };
      return unresolved('Price to be confirmed for the selected service.');
    }
    // A single amount cannot describe a choice of adult/child tariffs, percentage
    // supplements, minimum group offers, or a range.
    if (/\bchild(?:ren)?\b/.test(basis) && /\badult/.test(basis)) return unresolved('Adult and child rates depend on your party.');
    if (/%|\bpaying\b|\bFOC\b|\b(?:7|9)\s*\+\s*1\b/i.test(note)) return unresolved('This rate depends on the group or base package.');
    if (money.amount === 0) return { ...base, priceBasis: 'included', lineTotal: 0, pricingResolved: true };
    const guests = Math.max(0, Number(context.guests) || 0);
    const rooms = Math.max(0, Number(context.rooms) || 0);
    const nights = Math.max(1, Number(context.nights) || 1);
    const participants = extra.participants != null ? Math.max(0, Number(extra.participants) || 0) : guests;
    const quantity = extra.quantity != null ? Math.max(0, Number(extra.quantity) || 0) : null;
    const person = /per\s*(?:person|guest|pax|diver|participant|adult)\b|\bpp\b/.test(basis);
    const room = /per\s*(?:room|cabana|cabin|villa|bungalow|chalet|unit|cottage)\b/.test(basis);
    const daily = /(?:per|\/|-)\s*(?:(?:diving|dive)\s*)?(?:day|night)\b|\b\d+[- ]dive\s*(?:boat\s*)?day\b/.test(basis);
    const perDive = /(?:per|\/)\s*(?:applicable\s*)?(?:house-reef\s*)?(?:night\s*)?(?:dive|tank|fill)\b/.test(basis);
    const diveBundle = /\b(?:\d+|five|ten|fifteen|twenty)[- ](?:nitrox\s+|boat\s+|offshore\s+reef\s+)*(?:dives?|tank)\b/.test(label)
      && !/per\s*(?:diving\s*)?day|\/\s*day|\bdaily\b/.test(label);
    const explicitDaily = /\b\d+\s*dives?\s*(?:per|\/)\s*(?:diving\s*)?day|\b\d+[- ]dive\s*(?:boat\s*)?day|\bdaily diving\b|\bdives? daily\b/.test(combined);
    if (diveBundle && person && !daily && !perDive) return resolved(participants, 'per diver/package');
    if (person && daily) return resolved(participants * nights, 'per person/day');
    if (room && daily) return resolved(rooms * nights, 'per room/night');
    if (perDive && explicitDaily) {
      const count = combined.match(/\b(\d+)\s*(?:dives?\s*(?:per|\/)\s*(?:diving\s*)?day|[- ]dive\s*(?:boat\s*)?day)/);
      if (count) return resolved(participants * nights * Number(count[1]), `per diver/dive × ${count[1]} dives/day`);
    }
    if (perDive) return resolved(participants * (quantity ?? 1), `per diver/${/tank|fill/.test(basis) ? 'tank' : 'dive'} × ${quantity ?? 1}`);
    if (/per\s*(?:vehicle|car|van|minibus|boat)\b/.test(basis)) {
      const cap = label.match(/\b\d+\s*-\s*(\d+)\s*(?:guests?|pax|persons?|people)/);
      return resolved(cap ? Math.max(1, Math.ceil(guests / Number(cap[1]))) : 1, 'per vehicle/transfer');
    }
    if (person) return resolved(participants, 'per person/service');
    if (room) return resolved(rooms, 'per room/service');
    if (daily) return resolved(nights, 'per day');
    if (/per\s*(?:stay|booking|transfer|trip|activity)|flat\s*(?:fee|rate|price)|fixed\s*(?:fee|rate|price)|total\s*(?:fee|price)/.test(basis)) return resolved(1, 'fixed');
    return unresolved('Choose the service details to confirm its price.');
  }
  const api = { parseNumber, extractMoney, numericRate, currencyFor, formatMoney, normalizePriceUnit, priceUnitFor, packageNightsFromText, minimumStayFromText, normalizePricingMode, parseTierPrices, parseNightlyRates, pricingConfig, priceRoom, extraRole, priceExtra };
  scope.CteResortPricing = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window === 'undefined' ? globalThis : window);
